/**
 * DealVerdict — presentational renderer for a computed DealVerdict (P05).
 *
 * No DB access; safe in server and client trees. Renders nothing when the
 * verdict is null (Real-or-Hidden Rule 3 — the compute layer already refused
 * to make a thin claim). Every render carries the standing honesty line:
 * we have no MAP / cart-hidden price detection fields anywhere, so we
 * disclose that limitation instead of pretending to detect it.
 *
 * Visual idiom follows the existing PDP price-context badge: green pill for
 * the positive tiers (all-time-low, below-average), neutral gray for
 * typical/above-average.
 */

import type { DealVerdict as DealVerdictData } from './compute'
import { formatMonthYear } from './compute'

interface DealVerdictProps {
  verdict: DealVerdictData | null
  variant: 'pdp' | 'hub-card'
}

export function DealVerdict({ verdict, variant }: DealVerdictProps) {
  if (!verdict) return null

  const positive =
    verdict.tier === 'all-time-low' || verdict.tier === 'below-average'
  const pillClasses = positive
    ? 'text-green-800 bg-green-50 border-green-200'
    : 'text-gray-700 bg-gray-50 border-gray-200'
  const trackedSince = formatMonthYear(verdict.stats.trackedSinceDate)

  return (
    <div
      data-testid="deal-verdict"
      className={variant === 'pdp' ? 'mb-3' : 'mt-2'}
    >
      <div
        className={`inline-flex items-center gap-1.5 text-xs font-semibold border rounded-full px-3 py-1 ${pillClasses}`}
      >
        {positive && <span aria-hidden="true">↓</span>}
        <span>{verdict.headline}</span>
      </div>
      <p className="text-xs text-gray-600 mt-1">{verdict.detail}</p>
      {/* Retailer-neutral by owner directive (FRIDGE-350, 08/17/2026): the
          same sentence as the price-intelligence chart, naming no store. */}
      <p className="text-[11px] leading-snug text-gray-500 mt-1">
        Based on prices we&rsquo;ve tracked since {trackedSince}.
        Some retailers only show their lowest price in cart — we never guess
        those; verdicts use displayed prices only.
      </p>
    </div>
  )
}
